import { useState } from 'react';
import axios from 'axios';
import { Megaphone, Eye, Send, X, Loader2, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Feature-announcement broadcast card (Super Admin only).
 *
 * One-off announcement email to every live WC club admin about the new
 * leaderboard-broadcast feature. Preview-first UX to protect against
 * mis-sends. Idempotent — the server stamps each club so re-clicking
 * never double-sends.
 */
export default function FeatureAnnouncementCard({ authHeader }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const openPreview = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API}/admin/announcements/broadcast-feature-preview`, authHeader);
      setPreview(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const send = async () => {
    setSending(true);
    setError('');
    try {
      const r = await axios.post(`${API}/admin/announcements/send-broadcast-feature`, {}, authHeader);
      setResult(r.data);
      setPreview(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="card p-6" data-testid="feature-announcement-card">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center flex-shrink-0">
          <Megaphone size={20} />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-bold text-gray-900">Feature announcement · Leaderboard broadcast</h3>
          <p className="text-sm text-gray-500 mt-0.5">Emails every live WC club admin about the new &ldquo;send leaderboard to entrants&rdquo; button. Preview-first; idempotent (each club stamped, so re-clicking never sends twice).</p>
        </div>
      </div>

      {result && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-3 flex items-start gap-2" data-testid="fa-success">
          <CheckCircle2 size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-green-900">
            <strong>Sent {result.sent} of {result.total} announcements.</strong>
            {result.failed > 0 && <span className="text-amber-800"> {result.failed} failed — check Resend.</span>}
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-3 text-sm text-red-800" data-testid="fa-error">{error}</div>
      )}

      <button
        type="button"
        onClick={openPreview}
        disabled={loading}
        className="btn btn-sm bg-blue-600 text-white hover:bg-blue-700 text-xs px-4 py-2 rounded font-bold flex items-center gap-2 disabled:opacity-40"
        data-testid="fa-preview-btn"
      >
        {loading ? <><Loader2 size={14} className="animate-spin" /> Loading…</> : <><Eye size={14} /> Preview announcement</>}
      </button>

      {preview && (
        <FAPreviewModal
          preview={preview}
          onClose={() => setPreview(null)}
          onSend={send}
          sending={sending}
          error={error}
        />
      )}
    </div>
  );
}

function FAPreviewModal({ preview, onClose, onSend, sending, error }) {
  const { subject, html, sample_club: sample, eligible_recipient_count: count } = preview;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center px-4 py-6 bg-black/60" data-testid="fa-preview-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-0.5">Preview · sample club</p>
            <p className="text-sm font-semibold text-gray-900">{sample?.name} — {sample?.admin_email}</p>
            {preview.from && (
              <p className="text-xs text-gray-500 mt-0.5"><strong>From:</strong> {preview.from}</p>
            )}
            {preview.reply_to && (
              <p className="text-xs text-gray-500"><strong>Reply-To:</strong> {preview.reply_to}</p>
            )}
            <p className="text-xs text-gray-500 mt-0.5"><strong>Subject:</strong> {subject}</p>
            <p className="text-xs text-gray-500 mt-0.5">Will send to <strong>{count} eligible live WC club{count === 1 ? '' : 's'}</strong> (excludes any that&apos;ve already been notified).</p>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500" aria-label="Close" data-testid="fa-preview-close">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-hidden bg-gray-50 p-4">
          <iframe
            title="Announcement preview"
            srcDoc={html}
            className="w-full h-full min-h-[420px] bg-white border border-gray-200 rounded-lg"
            sandbox=""
            data-testid="fa-preview-iframe"
          />
        </div>

        <div className="px-5 py-4 border-t border-gray-200 bg-white">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3 text-sm text-red-800">{error}</div>
          )}
          <p className="text-xs text-gray-600 mb-3">
            Each recipient gets a copy with their own club&apos;s name + branding baked in. Once approved, the send is <strong>one-way</strong> — no undo.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              className="flex-1 py-2.5 text-sm font-bold text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40"
              data-testid="fa-preview-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSend}
              disabled={sending}
              className="flex-1 py-2.5 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="fa-preview-send"
            >
              {sending ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Send size={14} /> Approve &amp; send to {count} {count === 1 ? 'club' : 'clubs'}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
